/**
 * Mazing 3D Viewer Integration - Performance Optimized + SKU Check + Variant Sync + Multiply Background Layer
 */

(function () {
    'use strict';

    const DEBUG = false;
    const log = (...args) => DEBUG && console.log('[Mazing]', ...args);

    const customer = 'art-mind-shop';
    const isMobile = window.innerWidth < 768;

    let iframeSrc = null;
    let prefetchPromise = null;
    let viewerCreated = false;
    let initialSKU = null;

    let overlayButtonEl = null;
    let closeButtonEl = null;
    let iframeElementEl = null;
    let bgLayerEl = null; 
    let close3DViewerGlobal = null; 

    // =============================================
    // 1. Warte bis LCP durch ist
    // =============================================

    function startAfterLCP(callback) {
        if ('PerformanceObserver' in window) {
            let fired = false;
            const observer = new PerformanceObserver((list) => {
                if (fired) return;
                fired = true;
                observer.disconnect();
                requestIdleCallbackSafe(callback);
            });
            try {
                observer.observe({ type: 'largest-contentful-paint', buffered: true });
            } catch (e) {
                requestIdleCallbackSafe(callback);
            }
            setTimeout(() => {
                if (!fired) {
                    fired = true;
                    observer.disconnect();
                    requestIdleCallbackSafe(callback);
                }
            }, 3000);
        } else {
            requestIdleCallbackSafe(callback);
        }
    }

    function requestIdleCallbackSafe(callback) {
        if ('requestIdleCallback' in window) {
            requestIdleCallback(callback, { timeout: 2000 });
        } else {
            setTimeout(callback, 200);
        }
    }

    function waitForGlobal(checkFn, timeout = 15000) {
        return new Promise((resolve) => {
            if (checkFn()) { resolve(true); return; }
            const interval = 100;
            let elapsed = 0;
            const timer = setInterval(() => {
                if (checkFn()) { clearInterval(timer); resolve(true); return; }
                elapsed += interval;
                if (elapsed >= timeout) { clearInterval(timer); resolve(false); }
            }, interval);
        });
    }

    // =============================================
    // 2. SKU Helpers
    // =============================================

    const materialMap = {
        'galerieabzug': 'gallery-print', 'impression de galerie': 'gallery-print', 'gallery print': 'gallery-print',
        'acrylglas': 'acrylic-glass', 'verre acrylique': 'acrylic-glass', 'acrylic glass': 'acrylic-glass',
        'leinwand': 'canvas', 'toile': 'canvas', 'canvas': 'canvas',
        'brushed aluminium': 'brushed-aluminium', 'alu gebürstet': 'brushed-aluminium', 'aluminium brossé': 'brushed-aluminium', 'alu': 'brushed-aluminium'
    };

    const frameMap = {
        'ohne': 'without', 'sans': 'without', 'without': 'without',
        'schwarz': 'black', 'noir': 'black', 'black': 'black',
        'weiß': 'white', 'weiss': 'white', 'blanc': 'white', 'white': 'white'
    };

    function normalizePublicTitleToEnglish(publicTitle) {
        const parts = publicTitle.replace(/<\/?tc>/g, '').toLowerCase().split(/\s*\/\s*/);
        if (parts.length !== 3) return null;
        const material = materialMap[parts[0].trim()] || parts[0].trim().replace(/\s+/g, '-');
        const size = parts[1].trim().replace(/\s+/g, '');
        const frame = frameMap[parts[2].trim()] || parts[2].trim().replace(/\s+/g, '-');
        return `${material}-${size}-${frame}`;
    }

    function skuByVariant(variantID) {
        const variants = window.ShopifyAnalytics?.meta?.product?.variants;
        if (!variants) return false;
        for (const variant of variants) {
            if (variant.id === Number(variantID)) {
                const cleanSku = (variant.sku.match(/^\d+/)?.[0] || variant.sku).replace("'", "");
                const suffix = normalizePublicTitleToEnglish(variant.public_title);
                if (!suffix) return false;
                return `${cleanSku}-${suffix}`;
            }
        }
        return false;
    }

    // =============================================
    // 3. Styles (KUGELSICHER GEMACHT)
    // =============================================

    function injectMazingStyles() {
        if (document.getElementById('mazing-custom-styles')) return;
        const style = document.createElement('style');
        style.id = 'mazing-custom-styles';
        // HIER SIND DIE !important BEFEHLE, UM DIE THEME.CSS ZU BLOCKIEREN
        style.textContent = `
            .mazing-bg-layer{position:absolute;top:0;left:0;width:100%;height:100%;background-color:#f2f2f2;z-index:9;opacity:0 !important;visibility:hidden !important;pointer-events:none !important;}
            .mazing-iframe{border:0;height:100%;left:0;position:absolute;top:0;width:100%;background:transparent;mix-blend-mode:multiply;z-index:10;opacity:0 !important;visibility:hidden !important;pointer-events:none !important;}
            
            .mazing-bg-layer.is-visible, .mazing-iframe.is-visible{opacity:1 !important;visibility:visible !important;pointer-events:auto !important;}
            
            @keyframes mzFadeIn{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}
            @keyframes mzFadeOut{from{opacity:1;transform:scale(1)}to{opacity:0;transform:scale(.92)}}
            
            .mazing-bg-layer.is-opening, .mazing-iframe.is-opening{animation:mzFadeIn .35s cubic-bezier(.25,.46,.45,.94) forwards !important;visibility:visible !important;pointer-events:auto !important;}
            .mazing-bg-layer.is-closing, .mazing-iframe.is-closing{animation:mzFadeOut .25s cubic-bezier(.25,.46,.45,.94) forwards !important;visibility:visible !important;pointer-events:none !important;}
            
            .mazing-close-btn.is-opening{animation:mzFadeIn .3s cubic-bezier(.25,.46,.45,.94) .1s both}
            .mazing-viewer-open-btn.is-returning{animation:mzFadeIn .3s cubic-bezier(.25,.46,.45,.94) forwards}
        `;
        document.head.appendChild(style);
    }

    // =============================================
    // 4. Check & Load Logic
    // =============================================

    async function checkSKUAvailability(sku) {
        if (!overlayButtonEl || !sku) return;
        
        const ready = await waitForGlobal(() => typeof mazingProjectAvailable === 'function', 8000);
        if (!ready) return;

        const src = await mazingProjectAvailable({
            customerName: customer,
            getSKU: () => sku
        });

        if (src) {
            overlayButtonEl.style.display = 'flex';
            iframeSrc = src;
            prefetchPromise = Promise.resolve(src);
        } else {
            overlayButtonEl.style.display = 'none';
            iframeSrc = null;
            prefetchPromise = null;
        }
    }

    function prefetchMazingProject(sku) {
        if (!sku) return Promise.resolve(null);
        if (iframeSrc) return Promise.resolve(iframeSrc);
        
        if (!prefetchPromise) {
            prefetchPromise = (async () => {
                const ready = await waitForGlobal(() => typeof mazingProjectAvailable === 'function', 8000);
                if (!ready) return null;
                const src = await mazingProjectAvailable({ customerName: customer, getSKU: () => sku });
                iframeSrc = src;
                return src;
            })();
        }
        return prefetchPromise;
    }

    async function changeBySKU(sku) {
        if (!sku || sku === initialSKU) return;
        log('Variant changed! New SKU:', sku);

        initialSKU = sku; 
        prefetchPromise = null; 
        iframeSrc = null;
        
        // Zeile entfernt, damit der Button beim Variantenwechsel nicht flackert:
        // if (overlayButtonEl) overlayButtonEl.style.display = 'none'; 
        
        if (isMobile && typeof mazingRefreshSKU === 'function') {
            await mazingRefreshSKU(sku, customer);
        }

        await checkSKUAvailability(sku);

        if (iframeElementEl) {
            if (iframeSrc) {
                if (iframeElementEl.classList.contains('is-visible')) {
                    iframeElementEl.style.opacity = '0.5'; 
                    iframeElementEl.src = iframeSrc;
                    iframeElementEl.onload = () => iframeElementEl.style.opacity = '1';
                } else if (iframeElementEl.src && iframeElementEl.src !== 'about:blank') {
                    iframeElementEl.src = iframeSrc;
                }
            } else {
                iframeElementEl.removeAttribute('src');
                
                if (iframeElementEl.classList.contains('is-visible') && close3DViewerGlobal) {
                    close3DViewerGlobal();
                }
            }
        }
    }

    // =============================================
    // 5. 3D Viewer erstellen
    // =============================================

    function create3DViewer(mainPhotosElement) {
        if (viewerCreated) return;
        viewerCreated = true;

        mainPhotosElement.style.position = 'relative';
        injectMazingStyles();

        // 1. Die hellgraue Deckschicht
        bgLayerEl = document.createElement('div');
        bgLayerEl.className = 'mazing-bg-layer';
        mainPhotosElement.appendChild(bgLayerEl);

        // 2. Das Iframe
        iframeElementEl = document.createElement('iframe');
        iframeElementEl.id = 'mazing-iframe';
        iframeElementEl.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; camera *; xr-spatial-tracking;';
        iframeElementEl.allowFullscreen = true;
        iframeElementEl.setAttribute('allowusermedia', '');
        iframeElementEl.className = 'mazing-iframe';
        mainPhotosElement.appendChild(iframeElementEl);

        // 3. Close Button
        closeButtonEl = document.createElement('button');
        closeButtonEl.type = 'button';
        closeButtonEl.className = 'mazing-close-btn';
        closeButtonEl.setAttribute('aria-label', '3D Ansicht schließen');
        closeButtonEl.style.cssText = `
            position:absolute;top:14px;right:14px;z-index:15;width:46px;height:46px;
            background:rgba(0,0,0,.75);border:2px solid rgba(255,255,255,.3);border-radius:50%;
            cursor:pointer;display:none;align-items:center;justify-content:center;padding:0;
            line-height:1;transition:background .2s,transform .15s,border-color .2s;
            backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);
            box-shadow:0 2px 12px rgba(0,0,0,.25);
        `;
        closeButtonEl.innerHTML = '<svg width="20" height="20" viewBox="0 0 16 16" fill="none" style="display:block;margin:auto"><path d="M12 4L4 12M4 4l8 8" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/></svg>';
        
        closeButtonEl.addEventListener('mouseenter', () => {
            closeButtonEl.style.background = 'rgba(0,0,0,.92)';
            closeButtonEl.style.borderColor = 'rgba(255,255,255,.5)';
            closeButtonEl.style.transform = 'scale(1.1)';
        });
        closeButtonEl.addEventListener('mouseleave', () => {
            closeButtonEl.style.background = 'rgba(0,0,0,.75)';
            closeButtonEl.style.borderColor = 'rgba(255,255,255,.3)';
            closeButtonEl.style.transform = 'scale(1)';
        });
        mainPhotosElement.appendChild(closeButtonEl);

        // 4. Overlay Button
        overlayButtonEl = document.createElement('button');
        overlayButtonEl.type = 'button';
        overlayButtonEl.className = 'mazing-viewer-open-btn';
        overlayButtonEl.setAttribute('aria-label', '3D Ansicht öffnen');
        overlayButtonEl.style.display = 'none';

        const iconSize = isMobile ? 27 : 26;
        const assetBaseUrl = (document.querySelector('link[href*="/assets/"]')?.href || '').replace(/\/[^/]+$/, '');
        const iconUrl = assetBaseUrl ? assetBaseUrl + '/3D.svg' : '/cdn/shop/assets/3D.svg';
        const iconSvg = `<img src="${iconUrl}" alt="3D" width="${iconSize}" height="${iconSize}" loading="lazy" style="display:block;flex-shrink:0;pointer-events:none;transition:filter .25s;width:${iconSize}px;height:${iconSize}px" />`;

        if (isMobile) {
            overlayButtonEl.style.cssText = `
                position:absolute;bottom:20px;right:20px;z-index:5;width:48px;height:48px;
                min-width:unset;background:rgba(255,255,255,.92);border:2px solid #e8e8e8;
                border-radius:50%;padding:0;display:none;align-items:center;justify-content:center;
                cursor:pointer;transition:background .25s,border-color .25s,transform .2s;pointer-events:auto;
            `;
            overlayButtonEl.innerHTML = iconSvg;
        } else {
            overlayButtonEl.style.cssText = `
                position:absolute;bottom:16px;right:16px;z-index:5;height:46px;
                background:rgba(255,255,255,.94);border:1.5px solid #ddd;border-radius:5px;
                padding:0 18px;display:none;align-items:center;gap:10px;cursor:pointer;
                transition:background .25s,border-color .25s,transform .2s,box-shadow .25s;
                pointer-events:auto;backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);
                box-shadow:0 1px 6px rgba(0,0,0,.08);font-family:inherit;overflow:hidden;
            `;
            overlayButtonEl.innerHTML = iconSvg + `<span style="font-size:15px;font-weight:500;color:#333;letter-spacing:.02em;white-space:nowrap;transition:color .25s;line-height:1;pointer-events:none">In 3D ansehen</span>`;

            const iconEl = overlayButtonEl.querySelector('img');
            const textEl = overlayButtonEl.querySelector('span');

            overlayButtonEl.addEventListener('mouseenter', () => {
                overlayButtonEl.style.background = '#1a1a1a';
                overlayButtonEl.style.borderColor = '#1a1a1a';
                overlayButtonEl.style.transform = 'translateY(-1px)';
                overlayButtonEl.style.boxShadow = '0 3px 12px rgba(0,0,0,.15)';
                iconEl.style.filter = 'brightness(0) invert(1)';
                textEl.style.color = '#fff';
            });
            overlayButtonEl.addEventListener('mouseleave', () => {
                overlayButtonEl.style.background = 'rgba(255,255,255,.94)';
                overlayButtonEl.style.borderColor = '#ddd';
                overlayButtonEl.style.transform = 'translateY(0)';
                overlayButtonEl.style.boxShadow = '0 1px 6px rgba(0,0,0,.08)';
                iconEl.style.filter = 'none';
                textEl.style.color = '#333';
            });
        }

        mainPhotosElement.appendChild(overlayButtonEl);

        overlayButtonEl.addEventListener('mouseenter', () => prefetchMazingProject(initialSKU), { once: true });
        overlayButtonEl.addEventListener('touchstart', () => prefetchMazingProject(initialSKU), { once: true, passive: true });

        async function open3DViewer() {
            overlayButtonEl.style.opacity = '0.6';
            overlayButtonEl.style.pointerEvents = 'none';

            await prefetchMazingProject(initialSKU);

            overlayButtonEl.style.opacity = '';
            overlayButtonEl.style.pointerEvents = '';

            if (!iframeSrc) return;

            if (!iframeElementEl.src || iframeElementEl.src === 'about:blank' || iframeElementEl.src === window.location.href) {
                iframeElementEl.src = iframeSrc;
            }

            iframeElementEl.classList.remove('is-closing');
            bgLayerEl.classList.remove('is-closing');
            
            iframeElementEl.classList.add('is-visible', 'is-opening');
            bgLayerEl.classList.add('is-visible', 'is-opening');

            closeButtonEl.classList.add('is-opening');
            closeButtonEl.style.display = 'flex';

            overlayButtonEl.classList.remove('is-returning');
            overlayButtonEl.style.display = 'none';

            iframeElementEl.addEventListener('animationend', function h(e) {
                if(e.animationName !== 'mzFadeIn') return; // Safety check
                iframeElementEl.classList.remove('is-opening');
                bgLayerEl.classList.remove('is-opening');
                iframeElementEl.removeEventListener('animationend', h);
            });
        }

        function close3DViewer() {
            if (!iframeElementEl.classList.contains('is-visible') && !iframeElementEl.classList.contains('is-opening')) return;

            // Sofort is-visible wegnehmen, relies on is-closing animation
            iframeElementEl.classList.remove('is-opening', 'is-visible');
            bgLayerEl.classList.remove('is-opening', 'is-visible');

            iframeElementEl.classList.add('is-closing');
            bgLayerEl.classList.add('is-closing');

            closeButtonEl.style.display = 'none';
            closeButtonEl.classList.remove('is-opening');

            overlayButtonEl.style.display = 'flex';
            overlayButtonEl.classList.add('is-returning');

            iframeElementEl.addEventListener('animationend', function h(e) {
                if(e.animationName !== 'mzFadeOut') return; // Safety check
                iframeElementEl.classList.remove('is-closing');
                bgLayerEl.classList.remove('is-closing');
                iframeElementEl.removeEventListener('animationend', h);
            });
        }
        
        close3DViewerGlobal = close3DViewer;

        overlayButtonEl.addEventListener('click', (e) => { e.stopPropagation(); open3DViewer(); });
        closeButtonEl.addEventListener('click', (e) => { e.stopPropagation(); close3DViewer(); });

        const thumbScroller = document.querySelector('.product__thumbs--scroller');
        if (thumbScroller) {
            thumbScroller.addEventListener('click', (e) => {
                if (e.target.closest('.product__thumb-item')) close3DViewer();
            });
        }

        const emblaGallery = document.querySelector('[data-embla-gallery]');
        if (emblaGallery) {
            emblaGallery.addEventListener('embla:slideChange', () => close3DViewer());
        }

        if (isMobile) {
            let touchStartX = 0;
            mainPhotosElement.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
            mainPhotosElement.addEventListener('touchend', (e) => {
                if (Math.abs(e.changedTouches[0].clientX - touchStartX) > 50 && iframeElementEl.classList.contains('is-visible')) {
                    close3DViewer();
                }
            }, { passive: true });
        }
    }

    // =============================================
    // 6. Setup Variant Listener 
    // =============================================
    
    function setupVariantListener() {
        const variantInput = document.querySelector('[name="id"]');
        if (!variantInput) return;

        function checkVariant() {
            // NEU: Schließt den 3D Viewer bei jeder Änderung im Variant Picker
            if (typeof close3DViewerGlobal === 'function') {
                close3DViewerGlobal();
            }

            const sku = skuByVariant(variantInput.value);
            if (sku && sku !== initialSKU) {
                changeBySKU(sku);
            }
        }

        const form = document.querySelector('form[action^="/cart/add"]');
        if (form) {
            form.addEventListener('change', () => setTimeout(checkVariant, 50));
        }

        const originalReplaceState = history.replaceState;
        history.replaceState = function() {
            originalReplaceState.apply(this, arguments);
            setTimeout(checkVariant, 50);
        };
        
        const originalPushState = history.pushState;
        history.pushState = function() {
            originalPushState.apply(this, arguments);
            setTimeout(checkVariant, 50);
        };
    }

    async function setupABTest() {
        const ready = await waitForGlobal(() => typeof mazingSetAB === 'function', 10000);
        if (!ready) return;

        const addToCartBtn = document.querySelector('button.add-to-cart');
        if (!addToCartBtn) return;

        mazingSetAB({
            enabled: true,
            addToCartListeners: (callback) => {
                addToCartBtn.addEventListener('click', () => {
                    callback();
                });
            }
        });
    }

    // =============================================
    // 7. Initialisierung
    // =============================================

    function initInstantButton() {
        const mainPhotosElement = document.querySelector('.product__main-photos');
        if (!mainPhotosElement) return;

        const variantInput = document.querySelector('[name="id"]');
        if (variantInput) {
            initialSKU = skuByVariant(variantInput.value);
        }

        create3DViewer(mainPhotosElement);
        setupVariantListener();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initInstantButton);
    } else {
        initInstantButton();
    }

    startAfterLCP(() => {
        log('Post-LCP tasks running...');
        if (initialSKU) {
            checkSKUAvailability(initialSKU);
        }
        setupABTest();
    });

})();